import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { FormNumberInput } from 'src/shared/components/form-components';
import { FormMultiSelect } from 'src/shared/components/form-components/form-multiselect/form-multiselect';
import { Switcher } from 'src/shared/components/switcher/switcher';
import { Tooltip } from 'src/shared/components/tooltip';
import { hasValue } from 'src/shared/utils/common';

import { GeologicalLoadValidation } from '../../entities/geological-load/geological-load-validation';
import { GeologicalLoad } from '../../entities/geological-load/geological-load.entity';
import { GeologicalLoadViewModel } from '../geological-load-view-model';

import styles from './geological-item.module.scss';

type Props = {
  validation: GeologicalLoadValidation;
  geologicalLoad: GeologicalLoad;
  viewModel: GeologicalLoadViewModel;
  onValueChange(fieldId: string, value: unknown): void;
};

export const GeologicalItem = observer<Props>(function GeologicalItem({
  geologicalLoad,
  viewModel,
  validation,
  onValueChange,
}) {
  const { t } = useTranslation();

  const {
    coreSelection,
    testingFormationWork,
    dataInterpretation,
    coreSamplingIntervalId,
    testingFormationWorkAmount,
    numberOfDataInterpretationOperations,
    extractionVolume,
    coreSamplingDayAmount,
    testingFormationWorkDayAmount,
    daysAmountOfDataInterpretationOperation,
    errors,
    removeError,
  } = geologicalLoad;

  const extractionVolumeTextValue = hasValue(extractionVolume)
    ? Math.round(extractionVolume * 100) / 100
    : t('geologicalLoad:tips.extractionVolume');

  const coreSamplingDayAmountTextValue = hasValue(coreSamplingDayAmount)
    ? Math.round(coreSamplingDayAmount * 100) / 100
    : t('geologicalLoad:tips.coreSamplingDayAmount');

  const testingFormationWorkDayAmountTextValue = hasValue(testingFormationWorkDayAmount)
    ? Math.round(testingFormationWorkDayAmount * 100) / 100
    : t('geologicalLoad:tips.testingFormationWorkDayAmount');

  const daysAmountOfDataInterpretationOperationTextValue = hasValue(daysAmountOfDataInterpretationOperation)
    ? Math.round(daysAmountOfDataInterpretationOperation * 100) / 100
    : t('geologicalLoad:tips.daysAmountOfDataInterpretationOperation');

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={styles.switcherItem}>
          <p className={styles.switcherText}>{t('geologicalLoad:labels.coreSelection')}</p>
          <Switcher isActive={coreSelection} onToggle={() => onValueChange('coreSelection', !coreSelection)} />
        </div>

        <div className={styles.fieldItem}>
          <p className={styles.labelText}>{t('geologicalLoad:labels.coreSamplingInterval')}</p>
          <Tooltip
            text={t(errors.coreSamplingIntervalId ?? '')}
            placement="left"
            visible={errors.coreSamplingIntervalId ? undefined : false}
          >
            <div>
              <FormMultiSelect
                isDisabled={!coreSelection}
                className={styles.field}
                value={viewModel.getValidSelectValue('coreSamplingIntervalId', coreSamplingIntervalId)}
                options={viewModel.coreSamplingIntervalOptions.actual}
                placeholder={t('common:chooseValue')}
                isError={!!errors.coreSamplingIntervalId}
                onChange={(value) => onValueChange('coreSamplingIntervalId', value)}
                onFocus={() => removeError('coreSamplingIntervalId')}
                onBlur={() => validation.validateCoreSamplingIntervalId(geologicalLoad)}
              />
            </div>
          </Tooltip>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText}>{t('geologicalLoad:labels.extractionVolume')}</p>
          <p className={styles.readOnlyValueText}>{extractionVolumeTextValue}</p>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText}>{t('geologicalLoad:labels.coreSamplingDayAmount')}</p>
          <p className={styles.readOnlyValueText}>{coreSamplingDayAmountTextValue}</p>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.switcherItem}>
          <p className={styles.switcherText}>{t('geologicalLoad:labels.testingFormationWork')}</p>
          <Switcher
            isActive={testingFormationWork}
            onToggle={() => onValueChange('testingFormationWork', !testingFormationWork)}
          />
        </div>

        <div className={styles.fieldItem}>
          <p className={styles.labelText}>{t('geologicalLoad:labels.testingFormationWorkAmount')}</p>
          <Tooltip
            text={t(errors.testingFormationWorkAmount ?? '')}
            placement="left"
            visible={errors.testingFormationWorkAmount ? undefined : false}
          >
            <div>
              <FormNumberInput
                isInteger
                isDisabled={!testingFormationWork}
                className={styles.field}
                value={testingFormationWorkAmount}
                placeholder={t('common:enterValue')}
                isError={!!errors.testingFormationWorkAmount}
                unit={t('geologicalLoad:units.testingFormationWorkAmount')}
                onChange={(value) => onValueChange('testingFormationWorkAmount', value)}
                onFocus={() => removeError('testingFormationWorkAmount')}
                onBlur={() => validation.validateTestingFormationWorkAmount(geologicalLoad)}
              />
            </div>
          </Tooltip>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText}>{t('geologicalLoad:labels.testingFormationWorkDayAmount')}</p>
          <p className={styles.readOnlyValueText}>{testingFormationWorkDayAmountTextValue}</p>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.switcherItem}>
          <p className={styles.switcherText}>{t('geologicalLoad:labels.dataInterpretation')}</p>
          <Switcher
            isActive={dataInterpretation}
            onToggle={() => onValueChange('dataInterpretation', !dataInterpretation)}
          />
        </div>

        <div className={styles.fieldItem}>
          <p className={styles.labelText}>{t('geologicalLoad:labels.numberOfDataInterpretationOperations')}</p>
          <Tooltip
            text={t(errors.numberOfDataInterpretationOperations ?? '')}
            placement="left"
            visible={errors.numberOfDataInterpretationOperations ? undefined : false}
          >
            <div>
              <FormNumberInput
                isInteger
                isDisabled={!dataInterpretation}
                className={styles.field}
                value={numberOfDataInterpretationOperations}
                placeholder={t('common:enterValue')}
                isError={!!errors.numberOfDataInterpretationOperations}
                onChange={(value) => onValueChange('numberOfDataInterpretationOperations', value)}
                onFocus={() => removeError('numberOfDataInterpretationOperations')}
                onBlur={() => validation.validateNumberOfDataInterpretationOperations(geologicalLoad)}
              />
            </div>
          </Tooltip>
        </div>

        <div className={styles.readOnlyValueItem}>
          <p className={styles.labelText}>{t('geologicalLoad:labels.daysAmountOfDataInterpretationOperation')}</p>
          <p className={styles.readOnlyValueText}>{daysAmountOfDataInterpretationOperationTextValue}</p>
        </div>
      </div>
    </div>
  );
});
